import { CheckboxState, Theme, Tooltip } from "@landtechnologies/components";
import { useMemo } from "react";
import type {
  CheckboxTreeChange,
  HatchComponent,
  ParsedTreeNode,
  TreeSchema,
  TreeStructure,
} from "./types";
import { getAllDescendants } from "./utils";

//TODO: make sort and checkbox effect recurse downwards, rather than bubbling values upwards.
type TreeNodeInternal = ParsedTreeNode & {
  controlledNodes: string[];
  sortWeight: number;
};

const DEFAULT_SCHEMA = {
  showChildren: true,
};

interface ConstructTreeRecursive {
  treeStructure: TreeStructure;
  treeSchema: TreeSchema[];
  visibleNodeIds: string[];
  onChange: CheckboxTreeChange;
  Hatch?: HatchComponent;
}

export function constructTreeRecursive(props: ConstructTreeRecursive): TreeNodeInternal {
  const { treeStructure, treeSchema, visibleNodeIds, onChange, Hatch } = props;

  const partialConstruct = (t: TreeStructure) =>
    constructTreeRecursive({ ...props, treeStructure: t });

  const checkboxSchema = treeSchema.find(({ key }) => key == treeStructure.id) ?? DEFAULT_SCHEMA;

  // Which children should I display under me to the user.
  const childNodes =
    checkboxSchema.showChildren && treeStructure.nodes
      ? treeStructure.nodes.map(partialConstruct)
      : [];

  // Which children do I control and sent to the tile service.
  const controlledNodes = getAllDescendants(treeStructure).map(({ id }) => id);

  const handleCheckboxChange = (checked: boolean) => {
    onChange({
      id: treeStructure.id,
      checked: checked,
      visibleNodeIds: [
        ...visibleNodeIds.filter((k) => !controlledNodes.includes(k)),
        ...(checked ? controlledNodes : []),
      ],
    });
  };

  const checkboxState = controlledNodes.every((c) => visibleNodeIds.includes(c))
    ? CheckboxState.CHECKED
    : controlledNodes.some((c) => visibleNodeIds.includes(c))
    ? CheckboxState.INDETERMINATE
    : CheckboxState.EMPTY;

  const isLeafNode = !childNodes.length;
  let label = <>{treeStructure.display_name}</>;

  if (isLeafNode) {
    label = (
      <div className="atlas-flex atlas-gap-2 atlas-w-11/12">
        {Hatch && (
          <div className="atlas-flex-shrink-0 empty:atlas-hidden">
            <Hatch
              id={treeStructure.id}
              fillColorOverride={treeStructure.fillColorOverride}
              lineColorOverride={treeStructure.lineColorOverride}
            />
          </div>
        )}
        <div className="atlas-flex-shrink atlas-text-ellipsis atlas-overflow-hidden">
          <Tooltip.Wrapper>
            <Tooltip.Content align="start" side="bottom" theme={Theme.Dark} size="sm">
              <Tooltip.Header>{treeStructure.display_name}</Tooltip.Header>
              <Tooltip.Description>{treeStructure.description}</Tooltip.Description>
            </Tooltip.Content>
            <Tooltip.Trigger asChild>
              <div>{label}</div>
            </Tooltip.Trigger>
          </Tooltip.Wrapper>
        </div>
      </div>
    );
  }

  // If I am in the schema, use my ordering otherwise use my childs.
  // e.g. if schema is [green_belt, flood_risk_high] sort green_belt_outer before flood_risk_outer.
  const nodeIndex = treeSchema.findIndex(({ key }) => key == treeStructure.id);
  const sortWeight =
    nodeIndex >= 0
      ? nodeIndex
      : childNodes.map(({ sortWeight }) => sortWeight).reduce((p, c, _, a) => p + c / a.length, 0);

  const sortedNodes = [...childNodes].sort((a, b) => (a.sortWeight > b.sortWeight ? 1 : -1));

  return {
    id: treeStructure.id,
    label,
    checkboxState,
    handleCheckboxChange,
    childNodes: sortedNodes,
    isCollapsible: !isLeafNode,
    controlledNodes,
    sortWeight,
  };
}

const EMPTY_SCHEMA: TreeSchema[] = [];

interface UseCheckboxTree {
  treeStructure: TreeStructure;
  treeSchema?: TreeSchema[];
  visibleNodeIds: string[];
  onChange: CheckboxTreeChange;
  Hatch?: HatchComponent;
}

/**
 * The parent will always show if the child is present
 * If the parent is defined without a child, control all it's children and pretend to be leaf
 * If the parent is defined with a child, I am a collapsible treeNode
 */
export function useCheckboxTree({
  treeStructure,
  visibleNodeIds,
  treeSchema,
  onChange,
  Hatch,
}: UseCheckboxTree) {
  return useMemo(() => {
    return constructTreeRecursive({
      treeStructure,
      treeSchema: treeSchema ?? EMPTY_SCHEMA,
      visibleNodeIds,
      onChange,
      Hatch,
    });
  }, [treeStructure, treeSchema, visibleNodeIds, onChange, Hatch]);
}
